import { PublicRoute } from '@/components/containers';
import { AuthenticationStatus } from '@/modules/Auth/Auth.context';
import { ResetType } from '@/modules/Users/Users.schema';
import AddUser from '@/pages/AddUser';
import ChangePassword from '@/pages/ChangePassword';
import Customers from '@/pages/Customers';
import EditCustomer from '@/pages/EditCustomer';
import EditProject from '@/pages/EditProject';
import EditUser from '@/pages/EditUser';
import Error from '@/pages/Error';
import FarmDetails from '@/pages/FarmDetails';
import FarmsList from '@/pages/FarmsList';
import FieldDetails from '@/pages/FieldDetails';
import ForgotPassword from '@/pages/ForgotPassword';
import Login from '@/pages/Login';
import OtpSetup from '@/pages/OtpSetup';
import Profile from '@/pages/Profile';
import ProjectDetails from '@/pages/ProjectDetails';
import ProjectsList from '@/pages/ProjectsList';
import ResetPassword from '@/pages/ResetPassword';
import UserList from '@/pages/UserList';
import { logoutAction } from '@/utils/logout';
import { wrapCreateBrowserRouterV6 } from '@sentry/react';
import { createBrowserRouter, Outlet, redirect } from 'react-router-dom';

import { farmLoader, fieldLoader, projectLoader, protectedLoader } from '../Auth/Auth.utils';

import { IndexRoute } from './IndexRoute';
import { routes } from './Router.constants';

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = (authStatus: AuthenticationStatus, resetAuth: () => void) =>
  sentryCreateBrowserRouter([
    {
      path: routes.home,
      Component: Outlet,
      errorElement: <Error />,
      children: [
        { index: true, Component: IndexRoute, loader: protectedLoader() },
        {
          path: routes.customers,
          loader: protectedLoader(),
          element: <Customers />,
        },
        {
          path: routes.projects,
          loader: protectedLoader(),
          element: <ProjectsList />,
        },
        {
          path: routes.farms,
          loader: protectedLoader(),
          element: <FarmsList />,
        },
        {
          path: routes.users,
          loader: protectedLoader(),
          element: <UserList />,
        },
        {
          path: routes.addUser,
          loader: protectedLoader(),
          element: <AddUser />,
        },
        {
          path: routes.editUser,
          loader: protectedLoader(),
          element: <EditUser />,
        },
        {
          path: routes.customerProjectsList,
          loader: protectedLoader(),
          element: <ProjectsList />,
        },
        {
          path: routes.customerProjectFarmsList,
          loader: protectedLoader(),
          element: <FarmsList />,
        },
        {
          path: routes.editCustomer,
          loader: protectedLoader(),
          element: <EditCustomer />,
        },
        {
          path: routes.editProject,
          loader: protectedLoader(),
          element: <EditProject />,
        },
        {
          path: routes.customerProjectDetails,
          loader: projectLoader,
          element: <ProjectDetails />,
        },
        {
          path: routes.customerProjectFarmDetails,
          loader: farmLoader,
          element: <FarmDetails />,
        },
        {
          path: routes.customerProjectFarmFieldDetails,
          loader: fieldLoader,
          element: <FieldDetails />,
        },
        {
          path: routes.profile,
          loader: protectedLoader(),
          element: <Profile />,
        },
        {
          path: routes.settings,
          loader: protectedLoader(),
          element: <div>Settings</div>,
        },
        {
          path: routes.login,
          element: (
            <PublicRoute>
              <Login />
            </PublicRoute>
          ),
        },
        {
          path: routes.forgotPassword,
          element: (
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          ),
        },
        {
          path: routes.resetPassword,
          element: (
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          ),
        },
        {
          path: routes.changePassword,
          element: (
            <PublicRoute>
              <ChangePassword reset_type={ResetType.PASSWORD} />
            </PublicRoute>
          ),
        },
        {
          path: routes.otp_setup,
          element: (
            <PublicRoute>
              <OtpSetup />
            </PublicRoute>
          ),
        },
        {
          path: routes.onboarding,
          element: (
            <PublicRoute>
              <ChangePassword reset_type={ResetType.ONBOARDING} />
            </PublicRoute>
          ),
        },
      ],
    },
    {
      path: routes.logout,
      action() {
        logoutAction(resetAuth);
        return redirect(routes.login);
      },
    },
  ]);
